import { ReactElement, useCallback, useMemo } from 'react'
import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import TableChart from '@mui/icons-material/TableChart'
import { Divider, useTheme } from '@mui/material'
import useCreatePlanTable from '@editor/hooks/useCreatePlanTable'
import usePlanTableTool from '@editor/hooks/usePlanTableTool'
import { useQuery } from '@tanstack/react-query'
import { ToggleButtonDropDown, ToggleDropDownOptions } from '@ui/actions'
import { usePlanTableStore } from '@editorStores'
import { getPlanTableTemplates } from '@queries'
import type { PlanTableTemplate } from 'src/domain/planTables/types'
import { usePersistentParams } from 'src/hooks'

/** Option value of the "blank table" entry, which has no template key. */
const BLANK_TABLE_VALUE = 'blank'

interface Props {
  isAddPlanTableActive: boolean
  onToggleAddPlanTable: () => void
}

/**
 * Toolbar button for adding plan tables. Opens a dropdown (styled like the "Zeichnen" tool) to pick
 * a blank table or a predefined template before entering placement mode. Picking the already active
 * entry again leaves placement mode. Each entry's add button places the table directly instead.
 */
const PlanTableToolSelect = ({
  isAddPlanTableActive,
  onToggleAddPlanTable,
}: Props): ReactElement => {
  const theme = useTheme()
  const pendingTemplateKey = usePlanTableStore(state => state.pendingTemplateKey)
  const { startPlacement } = usePlanTableTool()
  const { quickAddPlanTable } = useCreatePlanTable()
  const { projectId }: { projectId?: string } = usePersistentParams()
  const { data: templates = [] } = useQuery({
    queryKey: getPlanTableTemplates.getKey(projectId),
    queryFn: () => getPlanTableTemplates.request(projectId),
    enabled: !!projectId,
  })

  const activeValue = isAddPlanTableActive ? pendingTemplateKey ?? BLANK_TABLE_VALUE : undefined

  const handleSelect = useCallback(
    (value: string) => {
      const templateKey = value === BLANK_TABLE_VALUE ? null : value

      if (isAddPlanTableActive && pendingTemplateKey === templateKey) {
        onToggleAddPlanTable()
        return
      }

      startPlacement(templateKey)
    },
    [isAddPlanTableActive, onToggleAddPlanTable, pendingTemplateKey, startPlacement],
  )

  const quickAddAction = useCallback(
    (value: string) => ({
      Icon: <AddCircleOutline fontSize="small" />,
      tooltip: 'Direkt auf dem Plan platzieren',
      onClick: () => void quickAddPlanTable(value === BLANK_TABLE_VALUE ? null : value),
      dataCy: `plan-table-quick-add-${value}`,
    }),
    [quickAddPlanTable],
  )

  const blankOptions = useMemo(
    () => [
      {
        label: 'Leere Tabelle',
        value: BLANK_TABLE_VALUE,
        dataCy: 'plan-table-template-blank',
        endAction: quickAddAction(BLANK_TABLE_VALUE),
      },
    ],
    [quickAddAction],
  )

  const templateOptions = useMemo(
    () =>
      templates.map((template: PlanTableTemplate) => ({
        label: template.name,
        value: template.key,
        dataCy: `plan-table-template-${template.key}`,
        endAction: quickAddAction(template.key),
      })),
    [templates, quickAddAction],
  )

  return (
    <ToggleButtonDropDown
      hint="Tabelle hinzufügen"
      openAbove
      tooltipPlacement="top"
      toggleType="click"
      closeOnChildrenClick
      style={{
        color: theme.palette.grey[800],
        backgroundColor: activeValue ? theme.palette.grey[400] : undefined,
        borderColor: theme.palette.grey[600],
      }}
      Icon={TableChart}
      data-cy="btn-toggle-plan-table-place"
    >
      <ToggleDropDownOptions value={activeValue} onClick={handleSelect} options={blankOptions} />
      {templateOptions.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <ToggleDropDownOptions
            value={activeValue}
            onClick={handleSelect}
            options={templateOptions}
          />
        </>
      )}
    </ToggleButtonDropDown>
  )
}

export default PlanTableToolSelect
